import { ErrorMessage, Field, Form, Formik } from 'formik';
import React, { useState } from 'react';
import * as Yup from 'yup';
import ErrorMessageComponent from '../components/ErrorMessage';
import LoadingSpinner from '../components/LoadingSpinner';
import { useAuth } from '../context/AuthContext';

const LoginSchema = Yup.object().shape({
  email: Yup.string()
    .email('Invalid email')
    .required('Email is required'),
  password: Yup.string()
    .required('Password is required')
});

const Login: React.FC = () => {
  const { login, loading } = useAuth();
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (values: { email: string; password: string }) => {
    try {
      setError(null);
      await login(values.email, values.password);
    } catch (err: any) {
      const message =
        err?.response?.data?.message ||
        err?.message ||
        'Login failed';
      setError(typeof message === 'string' ? message : 'Login failed');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-monday-bg flex items-center justify-center">
        <div className="text-center">
          <LoadingSpinner />
          <p className="mt-4 text-monday-text-muted">Signing you in...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-monday-bg relative overflow-hidden flex flex-col justify-center py-6 sm:py-12 px-4 sm:px-6 lg:px-8">
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_20%_15%,rgba(180,35,58,0.16),transparent_28rem),radial-gradient(circle_at_80%_80%,rgba(183,122,42,0.14),transparent_24rem)]" />
      {/* Mobile-friendly container */}
      <div className="relative sm:mx-auto sm:w-full sm:max-w-md">
        {/* App Logo/Icon */}
        <div className="mx-auto h-16 w-16 bg-gradient-to-br from-primary-500 to-primary-700 rounded-3xl flex items-center justify-center mb-6 shadow-primary-lg ring-1 ring-white/60">
          <svg className="h-8 w-8 text-white" fill="currentColor" viewBox="0 0 20 20">
            <path d="M10 2a6 6 0 00-6 6v3.586l-.707.707A1 1 0 004 14h12a1 1 0 00.707-1.707L16 11.586V8a6 6 0 00-6-6zm0 10a2 2 0 11-.001-3.999A2 2 0 0110 12z" />
          </svg>
        </div>

        <h2 className="text-center text-4xl font-extrabold tracking-tight text-monday-text mb-2">
          Wine Management
        </h2>
        <h3 className="text-center text-base text-monday-text-muted mb-2">
          Sign in to your account
        </h3>
        <p className="text-center text-sm text-monday-text-muted mb-8">
          <a href="/" className="font-semibold text-primary-600 hover:text-primary-700 underline-offset-2 hover:underline">
            Back to home
          </a>
        </p>
      </div>

      <div className="relative sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-white/90 backdrop-blur py-8 px-6 shadow-xl rounded-2xl border border-white/70 sm:px-10">
          {error && (
            <ErrorMessageComponent error={error} className="mb-6" />
          )}

          <Formik
            initialValues={{ email: '', password: '' }}
            validationSchema={LoginSchema}
            onSubmit={handleSubmit}
          >
            {({ isSubmitting, touched, errors }) => (
              <Form className="space-y-6">
                <div className="mobile-form-group">
                  <label htmlFor="email" className="block text-sm font-semibold text-monday-text mb-2">
                    Email Address
                  </label>
                  <div className="relative">
                    <Field
                      id="email"
                      name="email"
                      type="email"
                      autoComplete="email"
                      placeholder="Enter your email"
                      className={`appearance-none block w-full px-4 py-3 border rounded-xl shadow-xs placeholder-monday-text-muted focus:outline-none focus:ring-4 focus:ring-primary-100 focus:border-primary-500 text-base transition-all duration-200 bg-white/95 ${touched.email && errors.email
                        ? 'border-error-300 focus:ring-error-100 focus:border-error-500'
                        : 'border-monday-border'
                        }`}
                    />
                    {touched.email && errors.email && (
                      <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                        <svg className="h-5 w-5 text-error-400" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                        </svg>
                      </div>
                    )}
                  </div>
                  <ErrorMessage name="email" component="div" className="text-error-600 text-sm mt-2 flex items-center" />
                </div>

                <div className="mobile-form-group">
                  <label htmlFor="password" className="block text-sm font-semibold text-monday-text mb-2">
                    Password
                  </label>
                  <div className="relative">
                    <Field
                      id="password"
                      name="password"
                      type="password"
                      autoComplete="current-password"
                      placeholder="Enter your password"
                      className={`appearance-none block w-full px-4 py-3 border rounded-xl shadow-xs placeholder-monday-text-muted focus:outline-none focus:ring-4 focus:ring-primary-100 focus:border-primary-500 text-base transition-all duration-200 bg-white/95 ${touched.password && errors.password
                        ? 'border-error-300 focus:ring-error-100 focus:border-error-500'
                        : 'border-monday-border'
                        }`}
                    />
                    {touched.password && errors.password && (
                      <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                        <svg className="h-5 w-5 text-error-400" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                        </svg>
                      </div>
                    )}
                  </div>
                  <ErrorMessage name="password" component="div" className="text-error-600 text-sm mt-2 flex items-center" />
                </div>

                <div>
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full flex justify-center items-center py-3 px-4 border border-transparent rounded-full shadow-primary text-base font-semibold text-white bg-gradient-to-r from-primary-500 to-primary-600 hover:from-primary-600 hover:to-primary-700 focus:outline-none focus:ring-4 focus:ring-primary-100 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 hover:-translate-y-0.5 active:translate-y-0"
                  >
                    {isSubmitting ? (
                      <>
                        <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Signing in...
                      </>
                    ) : (
                      'Sign in'
                    )}
                  </button>
                </div>
              </Form>
            )}
          </Formik>

          <div className="mt-8">
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-monday-border-light" />
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-4 bg-white text-monday-text-muted font-medium">
                  Need access?
                </span>
              </div>
            </div>
            <div className="mt-4 text-center">
              <p className="text-sm text-monday-text-muted">
                Contact your administrator to get an account
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="mt-8 text-center">
        <p className="text-xs text-monday-text-muted">
          © 2024 Wine Management System. All rights reserved.
        </p>
      </div>
    </div>
  );
};

export default Login;
